'use client';
import { Input, Stack, VStack, Text, Box } from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import {
  DialogRoot,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTrigger,
  DialogBackdrop,
  DialogCloseTrigger,
  DialogContent,
} from '../ui/dialog';
import { useTranslations } from 'next-intl';
import { checkOmniAccount } from '@/lib/services/user.service';
import { Button } from '../ui/button';
import { toaster } from '@/components/ui/toaster';
import { useAppDispatch, useAppSelector } from '@/lib/hooks/storeHook';
import { selectEmailLoginOpen, setEmailLoginOpen } from '@/lib/stores/uiSlice';
import * as z from 'zod';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ModalCloseIcon } from '@/components/ui/svgs/modal-close';
import { Logo } from '../ui/svgs/logo';
import {
  // requestEmailVerificationCode,
  // requestAuthToken,
  // createAccountStore,
  requestEmailVerificationCodeWithRpc,
  requestPumpJwtWithRpc,
} from '@/lib/utils/omniApi.utils';
import { Back } from '../ui/svgs/back';
import { QueryClient } from '@tanstack/react-query';
import { useTranslateErrorMessage } from '@/lib/services/agent.service';

const queryClient = new QueryClient();
type Props = {
  onLogin: () => void;
};

// 1.verify email
// 2.login
// 3.register
type Step = 1 | 2 | 3;

export function EmailLogin({ onLogin }: Props): JSX.Element {
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [step, setStep] = useState<Step>(1);
  const [isBind2Fa, setIsBind2Fa] = useState(false);

  const t = useTranslations('Navigation.emailRegister');
  const tAuth = useTranslations('Auth');
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();
  const emailLoginOpen = useAppSelector(selectEmailLoginOpen);
  const { mutateAsync: translate } = useTranslateErrorMessage();

  const signUpSchema = useMemo(
    () =>
      z.object({
        email: z.string().email(tAuth('validation.emailInvalid')),
        verificationCode: z
          .string()
          .min(1, tAuth('validation.emailVerificationCodeRequired')),
        inviteCode: z.string().optional(),
        googleCode: isBind2Fa
          ? z.string().min(1, tAuth('validation.verificationCodeRequired'))
          : z.string().optional(),
      }),
    [tAuth, isBind2Fa],
  );

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    getValues,
    reset,
  } = useForm<z.infer<typeof signUpSchema>>({
    resolver: zodResolver(signUpSchema as any),
    mode: 'onChange',
    defaultValues: {
      email: '',
      verificationCode: '',
      inviteCode: '',
      googleCode: '',
    },
  });

  const login = async (data: z.infer<typeof signUpSchema>) => {
    setLoading(true);
    try {
      await requestPumpJwtWithRpc({
        email: data.email,
        verificationCode: data.verificationCode,
        googleCode: data.googleCode,
        inviteCode: data.inviteCode,
      });
      onLogin();
    } catch (error: any) {
      await translate({ text: error.message });
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = handleSubmit(login);

  const handleCheckAccount = async () => {
    try {
      const email = getValues('email');
      setCheckingEmail(true);
      const { isFirstLogin, isBind2Fa } = await checkOmniAccount({
        omniType: 1,
        omniValue: email,
      });
      setIsBind2Fa(isBind2Fa);
      await getEmailVerificationCode();
      if (isFirstLogin) {
        setStep(3);
      } else {
        setStep(2);
      }
    } catch (error: any) {
      toaster.create({
        title: error.message,
        type: 'error',
      });
    } finally {
      setCheckingEmail(false);
    }
  };

  const getEmailVerificationCode = async () => {
    try {
      await requestEmailVerificationCodeWithRpc(getValues('email'));
      setCountdown(60);
      const timer = setInterval(
        () =>
          setCountdown((prev) => {
            if (prev <= 1) {
              clearInterval(timer);
            }
            return prev - 1;
          }),
        1000,
      );
    } catch (error: any) {
      await translate({ text: error.message });
    }
  };

  return (
    <DialogRoot
      placement={'center'}
      open={emailLoginOpen}
      onOpenChange={(e) => {
        dispatch(setEmailLoginOpen(e.open));
        setStep(1);
        reset();
      }}
    >
      <DialogBackdrop />
      <DialogTrigger asChild>
        <Button
          px={6}
          rounded={8}
          bg="text.t1"
          color="bg.t1"
          fontWeight={500}
          fontSize="sm"
          lineHeight={1.5}
          size="xs"
          _hover={{
            opacity: 0.8,
          }}
        >
          {t('connect')}
        </Button>
      </DialogTrigger>

      <DialogContent
        maxW="480px"
        width="100%"
        borderRadius="20px"
        bg="bg.t1"
        border="1px solid"
        borderColor="text.t4"
      >
        <DialogHeader
          minH="52px"
          borderBottom="1px solid"
          borderColor="text.t4"
        >
          {step !== 1 && (
            <Box
              pos="absolute"
              left={5}
              top={5}
              cursor="pointer"
              onClick={() => {
                setStep(1);
                setCountdown(0);
              }}
            >
              <Back />
            </Box>
          )}
          <DialogCloseTrigger
            asChild
            top={5}
            right={5}
            w={5}
            h={5}
            minW="unset"
          >
            <ModalCloseIcon />
          </DialogCloseTrigger>
        </DialogHeader>

        <DialogBody>
          <Stack w="full" py={4} px={5} position="relative" gap={6}>
            {step === 1 && (
              <VStack w="full" gap={4}>
                <Logo />
                <Text fontSize="md" fontWeight={500} lineHeight={1.5}>
                  {t('registerTitle')}
                </Text>
              </VStack>
            )}
            {step !== 1 && (
              <VStack w="full" gap={1} align="start">
                <Text fontSize="sm" lineHeight={1.5} color="text.t2">
                  {t('sent_email')}
                </Text>
                <Text fontSize="xs" lineHeight={1.5} color="text.t1">
                  {getValues('email')}
                </Text>
              </VStack>
            )}
            {/* Email Input */}
            {step === 1 && (
              <VStack w="full" align="start" gap={1}>
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      placeholder={t('email')}
                      size="lg"
                      bg="transparent"
                      border="1px solid"
                      borderColor="text.t4"
                      rounded={8}
                      variant="outline"
                      py={'6px'}
                      px={3}
                    />
                  )}
                />
                {errors.email && (
                  <Text fontSize="11px" color="red.500" alignSelf="start">
                    {errors.email.message}
                  </Text>
                )}
              </VStack>
            )}
            {step !== 1 && (
              <Box position="relative" w="full">
                <VStack w="full" gap={1} align="start">
                  <Controller
                    name="verificationCode"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        placeholder={t('verificationCode')}
                        size="lg"
                        border="1px solid"
                        borderColor="text.t4"
                        rounded={8}
                        pr="100px"
                        py={'6px'}
                        px={3}
                      />
                    )}
                  />
                  {errors.verificationCode && (
                    <Text fontSize="11px" color="red.500" alignSelf="start">
                      {errors.verificationCode.message}
                    </Text>
                  )}
                </VStack>
                <Button
                  position="absolute"
                  right={2}
                  top={1}
                  size="sm"
                  variant="ghost"
                  color="text.primary"
                  disabled={countdown > 0}
                  onClick={getEmailVerificationCode}
                >
                  {countdown > 0 ? `${countdown}s` : t('getCode')}
                </Button>
              </Box>
            )}
            {step === 2 && isBind2Fa && (
              <VStack w="full" align="start" gap={1}>
                <Controller
                  name="googleCode"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      placeholder={t('googleAuth')}
                      size="lg"
                      bg="transparent"
                      border="1px solid"
                      borderColor="text.t4"
                      rounded={8}
                      variant="outline"
                      py={'6px'}
                      px={3}
                    />
                  )}
                />
                {errors.googleCode && (
                  <Text fontSize="11px" color="fixed.short" alignSelf="start">
                    {errors.googleCode.message}
                  </Text>
                )}
              </VStack>
            )}
            {step === 3 && (
              <Controller
                name="inviteCode"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    placeholder={t('invitationCode')}
                    size="lg"
                    border="1px solid"
                    borderColor="text.t4"
                    rounded={8}
                    py={'6px'}
                    px={3}
                  />
                )}
              />
            )}
          </Stack>
        </DialogBody>

        <DialogFooter>
          {/* Submit Button */}
          <VStack width="full" pb={5} px={5}>
            {step === 1 && (
              <Button
                loading={checkingEmail}
                size="lg"
                bg={!!errors.email ? 'text.t4' : 'text.t1'}
                rounded="full"
                color={!!errors.email ? 'text.t3' : 'bg.t1'}
                disabled={!!errors.email}
                w="full"
                _hover={{
                  opacity: 0.8,
                }}
                onClick={handleCheckAccount}
              >
                {t('getCode')}
              </Button>
            )}
            {step !== 1 && (
              <Button
                loading={loading}
                size="lg"
                bg={isValid ? 'text.t1' : 'text.t4'}
                rounded="full"
                color={isValid ? 'bg.t1' : 'text.t3'}
                disabled={!isValid}
                w="full"
                _hover={{
                  opacity: 0.8,
                }}
                onClick={onSubmit}
              >
                {t('connect')}
              </Button>
            )}
          </VStack>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
}
